import { FC, useEffect, useRef, useState } from 'react';
import { Icon } from './icons/Icon';
import { useAudio } from '../hooks/useAudio';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';
import WaveSurfer from 'wavesurfer.js';
import { PulseButton } from './ui/PulseButton';
import { COPY } from '../lib/copyDictionary';

export interface TimedComment {
    id: string;
    timestampSec: number;
    content: string;
    createdAt: number;
    user: {
        id: string;
        displayName: string;
        iconUrl: string;
    };
}

interface WaveformPlayerProps {
    audioUrl: string;
    title?: string;
    authorName?: string;
    imageUrl?: string;
    comments?: TimedComment[];
    onCommentClick?: (comment: TimedComment) => void;
    onAddComment?: (timestampSec: number, content: string) => void;
    isLoggedIn?: boolean;
}

export const WaveformPlayer: FC<WaveformPlayerProps> = ({
    audioUrl,
    title = '音声',
    authorName = 'クリエイター',
    imageUrl,
    comments = [],
    onCommentClick,
    onAddComment,
    isLoggedIn = false
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const wavesurferRef = useRef<WaveSurfer | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const { playSound } = useGamenics();
    
    // Global Audio
    const { currentTrack, isPlaying: globalIsPlaying, currentTime: globalCurrentTime, play, toggle, seek: globalSeek } = useAudio();
    const isThisTrack = currentTrack?.url === audioUrl;

    const [duration, setDuration] = useState(0);
    const [showCommentInput, setShowCommentInput] = useState(false);
    const [pendingTimestamp, setPendingTimestamp] = useState<number | null>(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const ws = WaveSurfer.create({
            container: containerRef.current,
            waveColor: '#cbc3d4',
            progressColor: '#ec4899', // neon-pink
            cursorColor: '#ec4899',
            barWidth: 2,
            barGap: 1,
            barRadius: 2,
            height: 80,
            normalize: true,
            backend: 'WebAudio',
        });

        ws.load(audioUrl);
        ws.setVolume(0); // Mute WaveSurfer, rely on GlobalPlayer for audio

        ws.on('ready', () => {
            const dur = ws.getDuration();
            setDuration(dur);
        });

        ws.on('decode', (duration: number) => {
            if (duration > 0) {
                setDuration(duration);
            }
        });

        ws.on('click', (progress: number) => {
            const timestamp = progress * ws.getDuration();
            if (isThisTrack) {
                globalSeek(timestamp);
            }
            if (isLoggedIn && onAddComment) {
                setPendingTimestamp(timestamp);
                setShowCommentInput(true);
            }
        });

        wavesurferRef.current = ws;

        return () => {
            ws.destroy();
        };
    }, [audioUrl, isLoggedIn, onAddComment, isThisTrack, globalSeek]);

    // Sync WaveSurfer UI with GlobalPlayer state
    useEffect(() => {
        if (!wavesurferRef.current) return;
        const ws = wavesurferRef.current;
        
        if (isThisTrack) {
            // Keep playhead synced
            if (duration > 0) {
                const targetProgress = globalCurrentTime / duration;
                // Avoid seeking if we are already playing and close to the time (to prevent jitter)
                const currentWsProgress = ws.getCurrentTime() / duration;
                if (Math.abs(targetProgress - currentWsProgress) > 0.05) {
                    ws.seekTo(targetProgress);
                }
            }
            
            // Sync play/pause state for animations
            if (globalIsPlaying && !ws.isPlaying()) {
                ws.play();
            } else if (!globalIsPlaying && ws.isPlaying()) {
                ws.pause();
            }
        } else {
            // If another track is playing globally, pause this local visualizer
            if (ws.isPlaying()) {
                ws.pause();
            }
        }
    }, [isThisTrack, globalIsPlaying, globalCurrentTime, duration]);

    const togglePlay = () => {
        playSound(isThisTrack && globalIsPlaying ? GamenicsEvent.TAP_CANCEL : GamenicsEvent.TAP_CONFIRM);
        
        if (isThisTrack) {
            toggle();
        } else {
            play({
                id: 'wave_' + Math.random().toString(36).substr(2, 9),
                url: audioUrl,
                title,
                authorName,
                imageUrl: imageUrl || null
            });
        }
    };

    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleAddComment = () => {
        if (pendingTimestamp !== null && onAddComment && inputRef.current?.value) {
            onAddComment(pendingTimestamp, inputRef.current.value);
        }
        setShowCommentInput(false);
        setPendingTimestamp(null);
    };

    const getCommentPositions = () => {
        if (duration === 0) return [];
        return comments.map(c => ({
            ...c,
            position: (c.timestampSec / duration) * 100
        }));
    };

    return (
        <div className="w-full bg-surface rounded-pop-md p-4 border-2 border-ink">
            <div className="relative">
                <div ref={containerRef} className="w-full" />

                <div className="absolute inset-0 pointer-events-none">
                    {getCommentPositions().map((c) => (
                        <div
                            key={c.id}
                            className="absolute top-0 h-full w-1 bg-primary/50 hover:bg-primary cursor-pointer pointer-events-auto transition-colors"
                            style={{ left: `${c.position}%` }}
                            onClick={() => onCommentClick?.(c)}
                            title={`${c.user.displayName}: ${c.content}`}
                        >
                            <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-primary animate-pulse" />
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex items-center justify-between mt-4">
                <PulseButton
                    onClick={togglePlay}
                    className="w-12 h-12 rounded-full flex items-center justify-center p-0 bg-ink text-sun border-2 border-ink"
                >
                    {isThisTrack && globalIsPlaying ? <Icon name="pause" size={16} /> : <Icon name="play" size={16} />}
                </PulseButton>

                <div className="text-ink-2 font-mono text-sm">
                    {formatTime(isThisTrack ? globalCurrentTime : 0)} / {formatTime(duration)}
                </div>

                {isLoggedIn && (
                    <PulseButton
                        variant="outline"
                        onClick={() => {
                            playSound(GamenicsEvent.TAP_CONFIRM);
                            const current = wavesurferRef.current?.getCurrentTime() || 0;
                            setPendingTimestamp(current);
                            setShowCommentInput(true);
                        }}
                    >
                        <Icon name="message" size={14} /> コメントを置く
                    </PulseButton>
                )}
            </div>

            {showCommentInput && pendingTimestamp !== null && (
                <div className="mt-4 p-4 bg-surface rounded-pop-md border-2 border-pop w-full shadow-lg animate-slide-up">
                    <p className="text-ink-2 text-sm mb-3">
                        @ {formatTime(pendingTimestamp)} にコメントを配置
                    </p>
                    <div className="flex gap-4 items-center">
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="コメントを入力..."
                            maxLength={200}
                            className="flex-1 min-w-0 bg-cream text-ink px-4 py-3 rounded-pop-md focus:outline-none focus:ring-2 focus:ring-pop border-2 border-ink"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleAddComment();
                            }}
                        />
                        <div className="flex gap-2 flex-shrink-0">
                            <PulseButton
                                isPrimary={true}
                                onClick={() => {
                                    playSound(GamenicsEvent.TAP_CONFIRM);
                                    handleAddComment();
                                }}
                            >
                                {COPY.submit}
                            </PulseButton>
                            <PulseButton
                                variant="ghost"
                                onClick={() => {
                                    playSound(GamenicsEvent.TAP_CANCEL);
                                    setShowCommentInput(false);
                                }}
                                className="text-ink-2"
                            >
                                {COPY.cancel}
                            </PulseButton>
                        </div>
                    </div>
                </div>
            )}

            {comments.length > 0 && (
                <div className="mt-4 space-y-2 max-h-40 overflow-y-auto">
                    {comments.slice(0, 10).map((c) => (
                        <div
                            key={c.id}
                            className="flex items-start gap-2 p-2 bg-gray-900/50 rounded-lg cursor-pointer hover:bg-gray-900/80"
                            onClick={() => {
                                wavesurferRef.current?.seekTo(c.timestampSec / duration);
                                onCommentClick?.(c);
                            }}
                        >
                            <img
                                src={c.user.iconUrl || '/default-avatar.png'}
                                alt={c.user.displayName}
                                className="w-6 h-6 rounded-full"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="text-ink-2">{c.user.displayName}</span>
                                    <span className="text-pop font-mono">@ {formatTime(c.timestampSec)}</span>
                                </div>
                                <p className="text-ink text-sm truncate">{c.content}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
